<template>
  <div
    class="rich-card"
    :class="[`rich-card--${theme.tone}`]"
  >
    <div class="rich-card__accent" :style="{ background: theme.accent }" />
    <div class="rich-card__body">
      <div class="rich-card__head">
        <div class="rich-card__icon" :style="{ color: theme.accent, background: theme.iconBg }">
          <component :is="theme.icon" />
        </div>
        <div class="rich-card__label">{{ card.label }}</div>
        <a-tooltip :title="card.description" placement="top">
          <question-circle-outlined class="rich-card__info" />
        </a-tooltip>
      </div>

      <div class="rich-card__value-row">
        <a-skeleton
          v-if="isLoading"
          active
          :title="{ width: '60%' }"
          :paragraph="false"
        />
        <div v-else class="rich-card__value">{{ displayValue }}</div>
      </div>

      <div v-if="hasSubStats" class="rich-card__stats">
        <span v-if="hasKey('goodCycles')" class="rich-card__stat">
          <span class="stat-dot stat-dot--good" />
          <span class="stat-value">{{ card.goodCycles }}</span>
          <span class="stat-label">Good</span>
        </span>
        <span v-if="hasKey('badCycles')" class="rich-card__stat">
          <span class="stat-dot stat-dot--bad" />
          <span class="stat-value">{{ card.badCycles }}</span>
          <span class="stat-label">Bad</span>
        </span>
        <span v-if="hasKey('timedOutCycles')" class="rich-card__stat">
          <span class="stat-dot stat-dot--timeout" />
          <span class="stat-value">{{ card.timedOutCycles }}</span>
          <span class="stat-label">Timed out</span>
        </span>
      </div>
      <div v-else class="rich-card__subtitle">{{ subtitle }}</div>
    </div>
  </div>
</template>

<script>
import {
  CalendarOutlined,
  ClockCircleOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons-vue';

const THEMES = {
  number_of_cycles: {
    icon: SyncOutlined,
    accent: '#1890ff',
    iconBg: 'rgba(24, 144, 255, 0.12)',
    tone: 'blue',
  },
  average_cycle_time: {
    icon: ClockCircleOutlined,
    accent: '#722ed1',
    iconBg: 'rgba(114, 46, 209, 0.12)',
    tone: 'purple',
  },
  total_cycles_time: {
    icon: CalendarOutlined,
    accent: '#13c2c2',
    iconBg: 'rgba(19, 194, 194, 0.12)',
    tone: 'teal',
  },
};
const DEFAULT_THEME = {
  icon: SyncOutlined,
  accent: '#1890ff',
  iconBg: 'rgba(24, 144, 255, 0.12)',
  tone: 'blue',
};

export default {
  components: { QuestionCircleOutlined },
  props: {
    card: { type: Object, required: true, default: () => ({}) },
    // Shows the value skeleton; each page passes its own loading state.
    isLoading: { type: Boolean, default: false },
  },
  computed: {
    theme() {
      return THEMES[this.card.key] || DEFAULT_THEME;
    },
    displayValue() {
      const v = this.card.value;
      return v == null || v === '' ? '—' : v;
    },
    hasSubStats() {
      return this.hasKey('goodCycles') || this.hasKey('badCycles') || this.hasKey('timedOutCycles');
    },
    subtitle() {
      // Small contextual hint under the primary value when no sub-stats.
      const key = this.card.key;
      if (key === 'average_cycle_time') return 'Mean of cycles in window';
      if (key === 'total_cycles_time') return 'Time actually worked';
      return '';
    },
  },
  methods: {
    hasKey(key) {
      return Object.prototype.hasOwnProperty.call(this.card, key);
    },
  },
};
</script>

<style scoped>
.rich-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #eef0f3;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  height: 100%;
}
.rich-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
}
.rich-card__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
}
.rich-card__body {
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
}
.rich-card__head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.rich-card__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  flex-shrink: 0;
}
.rich-card__label {
  flex: 1;
  min-width: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rich-card__info {
  color: #bfbfbf;
  font-size: 13px;
  cursor: help;
}
.rich-card__info:hover {
  color: #8c8c8c;
}
.rich-card__value-row {
  min-height: 36px;
  display: flex;
  align-items: baseline;
}
.rich-card__value {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1;
}
.rich-card__stats {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 4px;
  border-top: 1px dashed #f0f0f0;
  margin-top: auto;
}
.rich-card__stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}
.stat-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  display: inline-block;
}
.stat-dot--good {
  background: #52c41a;
}
.stat-dot--bad {
  background: #f5222d;
}
.stat-dot--timeout {
  background: #faad14;
}
.stat-value {
  font-weight: 700;
  color: #111827;
}
.stat-label {
  color: #6b7280;
}
.rich-card__subtitle {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: auto;
}
</style>
